export type PeelFamily = "gift" | "warning" | "upsell" | "action";

export interface PeelCandidate<Node> {
	id: string;
	family: PeelFamily;
	/** Breaks ties within a family. Family order always wins. */
	priority: number;
	node: Node;
	appearance?: "ticket";
	onDismiss?: () => void;
	/** A functional action owns its own face and dismissal, not the gift's paper treatment. */
	bare?: boolean;
	/**
	 * Where the tag and its pull sit. Below the card unless stated (ui#2949): the success card's invite,
	 * coupon and campaign hang BESIDE it, from its left edge. On a compact layout they wait as their tab:
	 * one with a `dialog` opens in it (see {@link peelTagOpensInDialog}), any other opens over the card when
	 * the reader asks (see {@link peelTagCollapsed}).
	 */
	placement?: "beside";
	/** The ×'s accessible name when it does more than tuck the tag away: the trial offer's × closes it. */
	dismissLabel?: string;
	/** The × closes the offer for good rather than tucking it to a tab (the trial offer). */
	closesForGood?: boolean;
	/**
	 * On a compact layout the tag waits as a tab on the card's edge and opens in a dialog instead of over
	 * the card or under it (owner 2026-10-06 and 2026-10-07, ui#3343, ui#3372); see
	 * {@link peelTagOpensInDialog}. A wide layout still hangs it where `placement` says. The value names the
	 * copy that tab and dialog carry, and `onDismiss` reports the dialog being closed as it reports a tuck.
	 */
	dialog?: PeelDialogKind;
}

/**
 * The tags that open in a dialog on a phone, by the copy their tab and dialog carry: the success cards'
 * coupon and the sender's own invite, and the recipient page's referral gift. `./peelDialogCopy` turns
 * one into its words.
 */
export type PeelDialogKind = "coupon" | "invite" | "recipientGift";
const familyPriority: Record<PeelFamily, number> = {gift: 4, warning: 3, upsell: 2, action: 1};
const COUPON_ID = "sender-coupon";

/** The one shape the coupon enters this placement with, whichever clause below admits it. */
function couponCandidate<Node>(coupon: Node, onCouponDismissed?: () => void): PeelCandidate<Node> {
	return {id: COUPON_ID, family: "gift", priority: 2, node: coupon, appearance: "ticket", onDismiss: onCouponDismissed,
		placement: "beside", dialog: "coupon"};
}

/**
 * The success placement has its own offer order; other placements retain their family order.
 *
 * This is what the slot is FREE to show. While the campaign is being asked about, what it may actually
 * show is narrower — {@link campaignRequestReserve} applies that, against this list.
 */
export function successGiftCandidates<Node>(signedIn: boolean, coupon: Node | null, invite: Node | null,
	campaign: Node | null = null, upsell: Node | null = null, onCouponDismissed?: () => void, couponLoading = false,
	couponExpired = false): PeelCandidate<Node>[] {
	// Expiry reserves this transfer's empty slot, including while the coupon is tucked.
	if (couponExpired) return coupon === null ? [] : [couponCandidate(coupon, onCouponDismissed)];
	// A pending coupon reserves the placement; a higher-priority campaign can still win.
	if (!signedIn || (couponLoading && campaign === null)) return [];
	const candidates: PeelCandidate<Node>[] = [];
	// Every offer on this card hangs beside it (owner 2026-09-26 and 2026-09-27, ui#2949), and the
	// campaign prints on the coupon's ticket because it is an offer too. Its impression stays a real
	// one: the campaign's node records it on mount, and a collapsed tag mounts no node until opened. On a
	// compact layout the coupon's ticket and the invite open in a dialog instead (ui#3343, ui#3372), and
	// mount only there.
	if (invite !== null) candidates.push({id: "sender-gift", family: "gift", priority: 0, node: invite, placement: "beside", dialog: "invite"});
	if (upsell !== null) candidates.push({id: "sender-upsell", family: "gift", priority: 1, node: upsell});
	if (coupon !== null) candidates.push(couponCandidate(coupon, onCouponDismissed));
	if (campaign !== null) candidates.push({id: "sender-campaign", family: "gift", priority: 3, node: campaign, appearance: "ticket", placement: "beside"});
	return candidates;
}

/**
 * The recipient page's referral gift ("A friend invited you to Transferify"): below the card on a wide
 * layout, as it always hung, and a tab on the card's edge that opens it in a dialog on a compact one
 * (owner 2026-10-07, ui#3372), where hanging under the card took the room the file list needs.
 *
 * @param gift the node to show, or null when the transfer offers no referral gift
 */
export function recipientGiftCandidates<Node>(gift: Node | null): PeelCandidate<Node>[] {
	return gift === null ? [] : [{id: "recipient-gift", family: "gift", priority: 0, node: gift, dialog: "recipientGift"}];
}

/**
 * The send screen's offer beside the card: the trial-expiry offer (owner 2026-09-27, ui#2949). It moved
 * out of the drop area to the card's left, where the success card's offers hang, and prints on the same
 * ticket. Its × closes the offer for good, as the callout's own × did, so `onDismiss` is that close.
 *
 * @param offer the node to hang, or null while there is no offer to show
 */
export function composingOfferCandidates<Node>(offer: Node | null, onDismiss: () => void, dismissLabel?: string): PeelCandidate<Node>[] {
	return offer === null ? [] : [{id: "trial-expiry-offer", family: "gift", priority: 0, node: offer,
		appearance: "ticket", placement: "beside", onDismiss, dismissLabel, closesForGood: true}];
}

/**
 * What the slot may show while the campaign request is in flight: what it was ALREADY showing, and
 * nothing else.
 *
 * The campaign outranks everything in this placement, so an unanswered request reserves the slot —
 * otherwise a lower-ranked tag appears in it and is displaced a moment later, which is a flash nobody
 * asked for. Withholding is only correct for a tag that is APPEARING though, and the slot's own memory
 * is the only thing that can tell the two apart (ui#2492):
 *
 * - a HOSTED card's first render opens its request with the card, so `held` is empty and the slot
 *   stays empty until the answer lands, exactly as before;
 * - an INSTANT card's request opens at the recipient's download, minutes after the api answered this
 *   send no coupon and the invite took the slot, so `held` names that invite and it stays mounted
 *   through the wait instead of unmounting and replaying its entrance when the campaign answers
 *   nothing, which is about sixteen days a month.
 *
 * The coupon is admitted whatever the memory says (ui#2486). Whether a coupon exists here at all
 * during a request is already decided upstream by `offers/ladder/couponCountdown`'s
 * `couponAdmissionKey`, which admits only one this screen had already received — so a second gate on
 * `held` could only take away a ticket the sender is looking at, which is the defect, not the rule.
 *
 * Nothing here re-ranks the slot: it removes candidates, and {@link resolvePeelTag} still chooses.
 *
 * @param held the ids the slot was free to show when the request opened, from
 *   `./useCampaignRequestReserve` — the adapter that remembers, since a rule cannot.
 */
export function campaignRequestReserve<Node>(candidates: readonly PeelCandidate<Node>[],
	held: readonly string[], campaignLoading: boolean): PeelCandidate<Node>[] {
	if (!campaignLoading) return [...candidates];
	return candidates.filter(candidate => candidate.id === COUPON_ID || held.includes(candidate.id));
}

/** Registration order breaks equal priorities. A tucked coupon reserves this transfer's empty slot. */
export function resolvePeelTag<Node>(candidates: readonly PeelCandidate<Node>[], dismissed: readonly string[]): PeelCandidate<Node> | null {
	if (dismissed.includes(COUPON_ID)) return null;
	return candidates.reduce<PeelCandidate<Node> | null>((selected, candidate) => {
		if (dismissed.includes(candidate.id)) return selected;
		if (selected === null || familyPriority[candidate.family] > familyPriority[selected.family]
			|| (candidate.family === selected.family && candidate.priority > selected.priority)) return candidate;
		return selected;
	}, null);
}

/**
 * A tucked coupon has a pull only while it is live; its expiry never reveals another offer. A bare
 * action holding the slot (the recipient's reply pull) is a control on the card, not a tag: it never
 * hides a tucked tag's pull, and never has a pull of its own.
 */
export function visiblePull<Node>(candidates: readonly PeelCandidate<Node>[], dismissed: readonly string[]): string | null {
	if (dismissed.includes(COUPON_ID)) return candidates.some(candidate => candidate.id === COUPON_ID) ? COUPON_ID : null;
	const tags = candidates.filter(candidate => !candidate.bare);
	if (resolvePeelTag(tags, dismissed) !== null) return null;
	return resolvePeelTag(tags, [])?.id ?? null;
}

/**
 * Whether the selected tag waits as its tab instead of showing its paper (ui#2949).
 *
 * On a compact layout a tag beside the card has no room beside it, so it opens OVER the card — and a
 * tag that opened over the card by itself would hide the link the sender just made. So there it
 * starts as its tab and opens only when the reader asks. On a wide layout it drops open as before.
 * Opening is per tag: a different tag taking the slot starts collapsed again.
 *
 * @param openedId the tag the reader opened from its tab, if any
 */
export function peelTagCollapsed<Node>(selected: PeelCandidate<Node> | null, compact: boolean, openedId: string | null): boolean {
	return compact && selected !== null && !selected.bare && selected.placement === "beside"
		&& !peelTagOpensInDialog(selected, compact) && openedId !== selected.id;
}

/**
 * Whether the selected tag waits as its tab and opens in a dialog instead of over the card or under it
 * (owner 2026-10-06 and 2026-10-07, ui#3343, ui#3372). Opened over a phone's card, the coupon's ticket and
 * the sender's invite hid its title and link, and the recipient's gift hanging under the card took the
 * room its file list needs, so on a compact layout each opens in the app's dialog. A wide layout keeps
 * each where it hangs, where it covers nothing.
 */
export function peelTagOpensInDialog<Node>(selected: PeelCandidate<Node> | null, compact: boolean): boolean {
	return compact && selected !== null && !selected.bare && selected.dialog !== undefined;
}

/**
 * Whether the card under the selected tag is dimmed (owner 2026-09-27, ui#2949): only while a beside tag
 * is OPEN over a compact card, so the ticket reads as lifted and the drop area as covered. A wide
 * layout's tag stands beside the card and covers nothing, so it dims nothing, and a tag that opens in a
 * dialog there never opens on the card at all.
 */
export function peelScrimShown<Node>(selected: PeelCandidate<Node> | null, compact: boolean, collapsed: boolean, leaving: boolean): boolean {
	return compact && !collapsed && !leaving && selected !== null && !selected.bare && selected.placement === "beside"
		&& !peelTagOpensInDialog(selected, compact);
}

/**
 * What a tap on that scrim does: the same as the ×, which tucks the tag to its tab — except for an offer
 * whose × closes it for good. A tap beside a ticket is not a decision to never see it again, so that
 * offer only closes back to its tab.
 */
export function peelScrimTucks<Node>(selected: PeelCandidate<Node>): boolean {
	return selected.closesForGood !== true;
}

/** Dismissal is independent of eligibility so a pre-empted candidate returns unchanged. */
export function setPeelTagDismissed(dismissed: readonly string[], id: string, dismiss: boolean): string[] {
	return dismiss ? [...new Set([...dismissed, id])] : dismissed.filter(candidate => candidate !== id);
}
